// A gallery card's picture: the letter as the engine renders it, scaled down.
//
// The render is `/preview` of a stored version — the same compile the sender
// runs, footer included — asked for only once the card is on screen, three at
// a time, and kept for the session: a version's content never changes after it
// is saved, so a render of version 42 is good for as long as the tab is open.

import { useEffect, useRef, useState } from "react";
import { ImageOffIcon } from "lucide-react";
import { previewVersion } from "@/lib/api";
import type { TemplatePreview } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { EMAIL_WIDTH, messageOf, reachNote } from "@/pages/templates/model";

const IN_FLIGHT = 3;
const renders = new Map<string, Promise<TemplatePreview>>();
const waiting: (() => void)[] = [];
let running = 0;

function queued<T>(run: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      running += 1;
      run()
        .then(resolve, reject)
        .finally(() => {
          running -= 1;
          waiting.shift()?.();
        });
    };
    if (running < IN_FLIGHT) start();
    else waiting.push(start);
  });
}

/** One render per (version, language, country), shared by every card and
 * every visit to the gallery; a failed one is forgotten so it can be retried. */
export function renderOf(versionId: number, language: string, country: string): Promise<TemplatePreview> {
  const key = `${versionId}:${language}:${country}`;
  let found = renders.get(key);
  if (found === undefined) {
    found = queued(() => previewVersion({ version_id: versionId, language, country }));
    renders.set(key, found);
    found.catch(() => renders.delete(key));
  }
  return found;
}

type State =
  | { status: "waiting" }
  | { status: "ok"; html: string }
  | { status: "error"; message: string };

interface Props {
  versionId: number;
  language: string;
  /** The country whose account renders it (`renderCountryOf`): any active
   * account when none speaks the language. Null when there is no active
   * account at all; undefined while the accounts are still being fetched. */
  country: string | null | undefined;
  className?: string;
}

export function Thumbnail({ versionId, language, country, className }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [state, setState] = useState<State>({ status: "waiting" });

  // On screen, or about to be: only then is the render worth asking for.
  useEffect(() => {
    const element = box.current;
    if (element === null || seen) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [seen]);

  useEffect(() => {
    const element = box.current;
    if (element === null) return undefined;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!seen || country === undefined) return undefined;
    if (country === null) {
      setState({ status: "error", message: reachNote({ kind: "nowhere" }, language) ?? "" });
      return undefined;
    }
    let alive = true;
    setState({ status: "waiting" });
    renderOf(versionId, language, country).then(
      (preview) => alive && setState({ status: "ok", html: preview.html }),
      (raised: unknown) => alive && setState({ status: "error", message: messageOf(raised) }),
    );
    return () => {
      alive = false;
    };
  }, [seen, versionId, language, country]);

  const scale = size.width > 0 ? size.width / EMAIL_WIDTH : 0;
  const height = size.height;

  return (
    <div
      ref={box}
      className={cn("relative aspect-[4/5] w-full overflow-hidden bg-surface-2", className)}
    >
      {state.status === "ok" && scale > 0 ? (
        <iframe
          title="A levél kicsinyített képe"
          aria-hidden="true"
          tabIndex={-1}
          // No scripts, no same-origin: a letter is markup, and a thumbnail
          // must not be able to do anything but be looked at.
          sandbox=""
          srcDoc={state.html}
          scrolling="no"
          className="pointer-events-none absolute top-0 left-0 origin-top-left border-0 bg-surface"
          style={{
            width: EMAIL_WIDTH,
            height: height > 0 ? Math.ceil(height / scale) : EMAIL_WIDTH,
            transform: `scale(${scale})`,
          }}
        />
      ) : state.status === "error" ? (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-label text-muted-foreground"
          title={state.message}
        >
          <ImageOffIcon className="size-5" />
          <span>A motor nem tudta megjeleníteni.</span>
          {/* The real reason, on the card: a blank picture says nothing. */}
          {state.message && <span className="line-clamp-4 text-danger break-words">{state.message}</span>}
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col gap-3 p-6" role="status" aria-label="Az előnézet készül">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      )}
    </div>
  );
}
